import React from "react";
import {
  Button,
  Grid,
  GridCell,
  Label,
  Select,
  SelectOption,
} from "@flixbus/honeycomb-react";
import { Icon, IconDelete, IconPlus } from "@flixbus/honeycomb-icons-react";
import type { EditDimensionValuesViewProps } from "./EditDimensionValuesView.types";
import styles from "./EditDimensionValuesView.module.scss";

const EMPTY_VALUE = "";

const EditDimensionValuesView: React.FC<EditDimensionValuesViewProps> = ({
  dimensionTypeName,
  seededValues,
  rows,
  onChangeRow,
  onAddRow,
  onRemoveRow,
  invalidRowIds,
}) => {
  const lowerName = dimensionTypeName.toLowerCase();
  const canAddRow = rows.length < seededValues.length;

  const optionsForRow = (rowValue: string) => {
    const usedElsewhere = new Set(
      rows
        .map((r) => r.value)
        .filter((v) => v !== EMPTY_VALUE && v !== rowValue),
    );
    return seededValues.filter((v) => !usedElsewhere.has(v));
  };

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        {`Add or remove ${lowerName}s. Each ${lowerName} stores its own cost values. Changes apply when you click `}
        <strong>Update</strong>
        {"."}
      </p>
      <ul className={styles.rowList}>
        {rows.map((row, index) => {
          const selectId = `edit-dim-row-${row.id}`;
          const options = optionsForRow(row.value);
          return (
            <li key={row.id}>
              <Grid align="start" gutter={2}>
                <GridCell colSpan={{ default: 10, sm: 8, md: 6 }}>
                  <Select
                    id={selectId}
                    label={<Label text={`${dimensionTypeName} ${index + 1}`} />}
                    placeholder="— Select —"
                    value={row.value}
                    valid={invalidRowIds?.has(row.id) ? false : undefined}
                    infoError={
                      invalidRowIds?.has(row.id)
                        ? "Please select a value."
                        : undefined
                    }
                    onChange={(e) => onChangeRow(row.id, e.target.value)}
                  >
                    <SelectOption value={EMPTY_VALUE}>
                      {"— Select —"}
                    </SelectOption>
                    {options.map((opt) => (
                      <SelectOption key={opt} value={opt}>
                        {opt}
                      </SelectOption>
                    ))}
                  </Select>
                </GridCell>
                <GridCell colSpan={2} extraClasses={styles.deleteCol}>
                  <Button
                    display="square"
                    appearance="link"
                    aria-label={`Remove ${dimensionTypeName} ${index + 1}`}
                    extraClasses={styles.deleteButton}
                    onClick={() => onRemoveRow(row.id)}
                  >
                    <Icon InlineIcon={IconDelete} aria-hidden="true" />
                  </Button>
                </GridCell>
              </Grid>
            </li>
          );
        })}
      </ul>
      <div>
        <Button
          appearance={"link"}
          disabled={!canAddRow}
          onClick={onAddRow}
          extraClasses={styles.addButton}
        >
          <span aria-hidden="true">
            <Icon InlineIcon={IconPlus} aria-hidden="true" />
          </span>
          {` Add ${lowerName}`}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(EditDimensionValuesView);
